import { useEffect, useRef, useState } from "react";
import { METRIC } from "@/lib/units";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Bot, Loader2, Send, X } from "lucide-react";
import axios from "axios";
import { useSession } from "@/context/SessionContext";
import { useLang } from "@/lib/i18n";
import { MicButton } from "@/components/MicButton";
import { UST_DATABASE } from "@/lib/tank";
import { ForwardForm } from "@/components/ForwardForm";
import { AskDev } from "@/components/AskDev";
import { isSimple } from "@/lib/simple";
import { HelpPhotoButton, PendingPhotos } from "@/components/HelpPhotos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/assistant/chat`;
const STORE = "fhp-help-chat-v1";
const HIDE_ON = ["/login", "/terms", "/account"];

const ROUTES = {
  find_drop: ["/", "drop"], find_receipt: ["/", "receipt"], find_level: ["/", "level"], find_ullage: ["/", "ullage"],
  find_line: ["/", "line"], find_volume: ["/", "volume"], scan: ["/"], drop: ["/drop"], chart: ["/chart"],
  tanks: ["/tanks"], sites: ["/sites"], login: ["/login"], account: ["/account"], terms: ["/terms"],
};
const ACTION_LABEL = {
  find_drop: "Before & after", find_receipt: "From receipt", find_level: "In the tank", find_ullage: "Ullage",
  find_line: "Fill line", find_volume: "To inches", scan: "Scan the console", drop: "Drop", chart: "Chart",
  tanks: "Tanks", sites: "Sites & History", login: "Sign in", account: "Account", terms: "Disclaimer & Terms",
};
const SIMPLE_LABEL = {
  find_drop: "Before & after drop", find_receipt: "Console printout", find_level: "Stick + console gallons",
  drop: "Plan a drop", chart: "Tank chart", tanks: "Tank sizes", sites: "My stations & past drops",
};
const SUGGESTIONS = [
  "Which tank am I dropping into?", "How do I read the console receipt?",
  "Is my drop safe?", "How do I save a station with several tanks?",
];

const load = () => { try { return JSON.parse(sessionStorage.getItem(STORE)) || []; } catch { return []; } };

function Bubble({ m, onGo, t, id, onSent }) {
  return (
    <div className={`bubble bubble-${m.role}`} data-testid={`help-msg-${m.role}`} data-no-metric="">
      {m.role === "user" && m.photos?.length > 0 && (
        <div className="photo-row bubble-photos" data-testid={`help-msg-photos-${id}`}>
          {m.photos.map((p) => <div key={p.id} className="photo-thumb photo-thumb-sm"><img src={p.preview} alt="" /></div>)}
        </div>
      )}
      <p>{m.content}</p>
      {m.forward && <ForwardForm question={m.question || ""} t={t} id={id} done={m.forwarded} onSent={onSent} initialPhotos={m.photos} />}
      {m.action && ROUTES[m.action] && (
        <button type="button" className="go-btn" data-testid={`help-go-${m.action}`} onClick={() => onGo(m.action, m.fields)}>
          {t(Object.keys(m.fields || {}).length ? "Fill in & open {x}" : "Take me to {x}", { x: t((isSimple() && SIMPLE_LABEL[m.action]) || ACTION_LABEL[m.action]) })}<ArrowRight size={14} />
        </button>
      )}
    </div>
  );
}

export function HelpBot() {
  const { lang, t } = useLang();
  const { patch } = useSession();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState(load);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener("open-help", show);
    return () => window.removeEventListener("open-help", show);
  }, []);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { try { sessionStorage.setItem(STORE, JSON.stringify(msgs.slice(-20))); } catch { /* full */ } }, [msgs]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs, busy, open]);

  const ask = async (q) => {
    const attached = photos;
    const question = q.trim() || (attached.length ? t("What does this photo show?") : "");
    if (!question || busy || uploading) return;
    const next = [...msgs, { role: "user", content: question, photos: attached }];
    setMsgs(next); setText(""); setPhotos([]); setBusy(true);
    try {
      const { data } = await axios.post(API, { lang, metric: METRIC, photo_ids: attached.map((p) => p.id), messages: next.slice(-10).map(({ role, content }) => ({ role, content })) }, { timeout: 60000 });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply, action: data.action, fields: data.fields || {}, forward: !!data.forward, question: data.question || question, photos: data.forward ? attached : undefined }]);
    } catch (e) {
      const msg = e.response?.status === 429 ? t("Too many questions. Wait a moment and try again.") : t("The helper didn't answer. Check your signal and try again.");
      setMsgs((m) => [...m, { role: "assistant", content: msg, error: true }]);
    } finally { setBusy(false); }
  };

  const go = (action, fields = {}) => {
    const [route, mode] = ROUTES[action];
    const spec = fields.capacity && UST_DATABASE.find((x) => x.capacity === fields.capacity);
    if (fields.capacity) Object.assign(fields, spec ? { diameter: spec.diameter, ends: spec.ends } : {});
    if (mode || Object.keys(fields).length) patch({ ...(mode ? { solverMode: mode } : {}), ...fields });
    setOpen(false);
    nav(route);
  };

  if (HIDE_ON.includes(pathname)) return null;
  return (
    <>
      {open && (
        <div className="help-backdrop" data-testid="help-bot-panel" onClick={() => setOpen(false)}>
          <div className="help-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={t("Help assistant")}>
            <div className="help-head">
              <div className="help-avatar"><Bot size={18} /></div>
              <div className="header-text"><div className="header-title">{t("Help assistant")}</div>
                <div className="header-sub">{t("Ask how to do anything in the app")}</div></div>
              <AskDev compact testid="help-ask-dev" />
              <button type="button" className="header-btn" data-testid="help-close" aria-label={t("Close")} onClick={() => setOpen(false)}><X size={20} /></button>
            </div>
            <div className="help-body" data-testid="help-messages">
              {msgs.length === 0 && (
                <div className="help-intro">
                  <p>{t("Hi! Tell me what you're trying to do and I'll point you to the right screen.")}</p>
                  <div className="help-suggest">
                    {SUGGESTIONS.map((s, i) => (
                      <button key={s} type="button" className="chip" data-testid={`help-suggestion-${i}`} onClick={() => ask(t(s))}>{t(s)}</button>
                    ))}
                  </div>
                </div>
              )}
              {msgs.map((m, i) => <Bubble key={i} id={i} m={m} onGo={go} t={t}
                onSent={() => setMsgs((all) => all.map((x, j) => (j === i ? { ...x, forwarded: true } : x)))} />)}
              {busy && <div className="bubble bubble-assistant" data-testid="help-typing"><Loader2 size={16} className="spin" /></div>}
              <div ref={endRef} />
            </div>
            <PendingPhotos photos={photos} setPhotos={setPhotos} t={t} />
            <form className="help-input" onSubmit={(e) => { e.preventDefault(); ask(text); }}>
              <input className="text-input" data-testid="help-input" placeholder={photos.length ? t("Ask about the photo…") : t("Type or tap the mic…")} value={text}
                maxLength={500} enterKeyHint="send" autoComplete="off" onChange={(e) => setText(e.target.value)} />
              <HelpPhotoButton photos={photos} setPhotos={setPhotos} busy={uploading} setBusy={setUploading} disabled={busy} t={t}
                onError={(msg) => setMsgs((m) => [...m, { role: "assistant", content: msg, error: true }])} />
              <MicButton lang={lang} t={t} disabled={busy} onText={(q) => ask(q)}
                onError={(msg) => setMsgs((m) => [...m, { role: "assistant", content: msg, error: true }])} />
              <button type="submit" className="send-btn" data-testid="help-send" disabled={(!text.trim() && !photos.length) || busy || uploading} aria-label={t("Send")}><Send size={18} /></button>
            </form>
            <p className="help-note">{t("AI answers can be wrong. Always verify readings with the console.")}</p>
          </div>
        </div>
      )}
    </>
  );
}
